<template>
  <div class="py-6 px-4 prose prose-sm prose-blue sm:px-6">

    <h1 ref="title" class="text-2xl font-semibold mb-0">
      ID Check
    </h1>
    <p class="lead mt-0">
      Complete a straightforward ID check before publishing your profile.
    </p>

    <div
      v-if="screen === 'processing'"
      class="-mx-4 mt-4 bg-blue-50 p-4 sm:mx-0 sm:rounded-md"
    >
      <div class="flex">
        <div class="hidden flex-shrink-0 sm:block">
          <Spinner color="#999" size="1.8em" />
        </div>
        <div class="sm:ml-3">
          <div class="flex items-center space-x-2 sm:space-x-0">
            <Spinner color="#999" size="1.2em" class="-mt-2 sm:hidden" />
            <h3 class="mt-0 text-sm font-medium text-blue-800">Your ID Check is being processed.</h3>
          </div>
          <div class="mt-2 prose prose-sm">
            <p>
              This should only take a few minutes.
              If you have any questions, please feel free to contact us at <a href="mailto:support@thetutorindex.com">support@thetutorindex.com</a>.
            </p>    
          </div>
        </div>
      </div>

    </div>
    <div
      v-else-if="screen === 'verified'"
      class="-mx-4 mt-4 bg-green-50 p-4 sm:mx-0 sm:rounded-md"
    >
      <div class="flex items-center space-x-2">
        <CheckCircleIcon class="w-8 h-8 text-green-600" />
        <h3 class="m-0 text-sm font-medium text-green-800">You have passed our ID check.</h3>
      </div>

    </div>
    <div
      v-else-if="screen === 'requires_input'"
      class="-mx-4 mt-4 bg-red-50 p-4 sm:mx-0 sm:rounded-md"
    >
      <div class="flex">
        <div class="hidden flex-shrink-0 sm:block">
          <XCircleIcon class="w-6 h-6 text-red-600" />
        </div>
        <div class="sm:ml-3">
          <div class="flex items-center space-x-2 sm:space-x-0">
            <XCircleIcon class="w-6 h-6 text-red-600 -mt-2 sm:hidden" />
            <h3 class="mt-0 text-sm font-medium text-red-800">
              We have not been able to verify your identity.
            </h3>
          </div>
          <div class="mt-2 prose prose-sm">
            <p>
              Please contact us at <a href="mailto:support@thetutorindex.com">support@thetutorindex.com</a> for assistance.
            </p>    
          </div>
        </div>
      </div>

    </div>
    <template v-else>

      <p>
        Our mandatory ID check helps reassure prospective clients that the profiles they see on The Tutor Index are accurate and trustworthy.
      </p>
      <div
        v-if="$store.getters.user.Published"
        class="-mx-4 mt-4 bg-yellow-50 p-4 sm:mx-0 sm:rounded-md"
      >
        <div class="flex">
          <div class="hidden flex-shrink-0 sm:block">
            <InformationCircleIcon class="w-6 h-6 text-yellow-600" />
          </div>
          <div class="sm:ml-3">
            <div class="flex items-center space-x-2 sm:space-x-0">
              <InformationCircleIcon class="w-6 h-6 text-yellow-600 -mt-2 sm:hidden" />
              <h3 class="mt-0 text-sm font-medium text-yellow-800">
                Act now to avoid disruption!
              </h3>
            </div>
            <div class="mt-2 prose prose-sm">
              <p>
                Profiles published before 9<sup>th</sup> May 2022 were not required to pass an ID Check.
              </p>    
              <p>
                However, this will soon become a mandatory requirement for all profiles, including those previously published.
              </p>
              <p>
                Complete our ID Check now to avoid your profile becoming unpublished and unavailable for prospective clients.
              </p>
            </div>
          </div>
        </div>

      </div>
      <p>
        The ID check is securely carried out by Stripe™, and should only take about 1 minute to complete.
      </p>
      <p>
        You will need a government-issued identity document, such as a passport, driving licence, or identity card.
        You will also need the ability to take a selfie, for example a phone camera or webcam.
      </p>
      <p>
        If you have any questions, please feel free to contact us at <a href="mailto:hello@thetutorindex.com">hello@thetutorindex.com</a>.
      </p>

      <TailwindButton
        @click="startIdCheck"
        :loading="loading"
        :disabled="loading"
      >
        Start ID Check
      </TailwindButton>

    </template>

  </div>
</template>

<script>
import TailwindButton from "./TailwindButton.vue";
import IdCheckServices from '@/services/IdCheckServices'
const idCheckService = new IdCheckServices()
import Spinner from './Spinner.vue'
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon } from '@vue-hero-icons/solid'

let processingTimer = null

export default {
  components: { TailwindButton, Spinner, CheckCircleIcon, XCircleIcon, InformationCircleIcon },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    screen() {
      return this.$store.getters.user.IdCheckStatus
    }
  },
  watch: {
    screen: {
      immediate: true,
      handler(newVal) {
        if (!newVal || newVal === 'verified' || newVal === 'requires_input') {
          clearInterval(processingTimer)
        }
        else {
          this.startCheckingForStatusChanges()
        }
      },
    }
  },
  methods: {
    async startIdCheck() {
      this.loading = true
      const response = await idCheckService.generateVerificationSession()
      this.loading = false
      this.startCheckingForStatusChanges()
      window.open(response.data, 'stripe')
    },
    async checkProcessingIdStatus() {
      const response = await idCheckService.getIdCheckStatus()
      this.$store.dispatch('updateUser', { IdCheckStatus: response.data })
    },
    startCheckingForStatusChanges() {
      clearInterval(processingTimer)
      processingTimer = setInterval(this.checkProcessingIdStatus, 5000)
    }
  },
  beforeRouteLeave(to, from, next) {
    clearInterval(processingTimer)
    next()
  }
}
</script>

<style lang="scss" scoped>

</style>